/**
 * A BOARD IS A VIEW OF MILESTONES, NOT A STATUS FIELD.
 *
 * That sentence is the whole design, and it comes straight out of the linked
 * boards contract: a job marked Complete is not a paid invoice, and a lead
 * marked Won by an external CRM is not a signed proposal. The moment a column
 * becomes a field somebody types into, the board starts disagreeing with the
 * records underneath it, and then it is worse than no board - because people
 * believe it.
 *
 * So a card's column is COMPUTED, every time, from things that actually
 * happened: a design exists, a quote was sent, a scope was approved, the job
 * was marked lost. Nothing writes a column anywhere. Moving a card is a
 * request to perform the underlying action, and a move that has no underlying
 * action to perform is refused with the reason rather than quietly writing a
 * new column.
 *
 * WHAT IS DELIBERATELY MISSING. Lead source, owner, age and commercial versus
 * residential are full production controls in the contract and there is
 * nothing on a job carrying any of them, so they are not invented here. The
 * Jobs board - awaiting approval, planning, ready to schedule, scheduled, in
 * progress, complete - needs reservation, scheduling and crew events that do
 * not exist in this product yet; four of its six columns would be permanently
 * empty, so it is not shipped as a board pretending otherwise.
 */

export type LeadStage = 'new' | 'designing' | 'sent' | 'won' | 'lost'

export const LEAD_STAGES: { id: LeadStage; label: string; blurb: string }[] = [
  { id: 'new', label: 'New', blurb: 'A name, and nothing drawn yet.' },
  { id: 'designing', label: 'Designing', blurb: 'Lights on the photo, nothing sent.' },
  { id: 'sent', label: 'Proposal sent', blurb: 'They have the quote.' },
  { id: 'won', label: 'Won', blurb: 'They said yes.' },
  { id: 'lost', label: 'Lost', blurb: 'They went elsewhere, or the season passed.' },
]

/** Everything the board is allowed to read. All of it already exists on a job. */
export interface LeadFacts {
  id: string
  customer: string
  address: string
  name: string
  /** The job's own state, as the list has always carried it. */
  status: 'draft' | 'quoted' | 'won' | 'lost'
  /** Anything drawn on the photograph. */
  layers: number
  /** True when a scope has been frozen. See lib/approval.ts. */
  scopeApproved: boolean
  /** What the customer owes, if there is paper at all. */
  balance: number
  hasPaper: boolean
}

/**
 * WHICH COLUMN THIS CARD IS IN.
 *
 * Read top down, and the order is the argument. Lost is first because a
 * contractor who wrote a job off does not want it back in his pipeline
 * because a scope happens to be approved. An approved scope is next, because
 * a customer agreeing to an exact revision IS won, and the contract says so:
 * that is what makes an approval move the card without anybody dragging it.
 */
export function leadStage(j: LeadFacts): LeadStage {
  if (j.status === 'lost') return 'lost'
  if (j.status === 'won' || j.scopeApproved) return 'won'
  if (j.status === 'quoted') return 'sent'
  if (j.layers > 0) return 'designing'
  return 'new'
}

export interface Move {
  /** Allowed at all. A refusal carries the reason instead. */
  ok: boolean
  /**
   * What actually happens underneath. Never "set the column": every one of
   * these is an event on the job, and the column follows from it.
   */
  does: string
  /** Why not, when it cannot be done. */
  because?: string
  /** The job state this move writes, where it writes one. */
  status?: 'draft' | 'quoted' | 'won' | 'lost'
}

/**
 * WHAT DRAGGING THIS CARD THERE WOULD ACTUALLY DO.
 *
 * Called before anything is committed, so a screen can say what will happen
 * and, where it will not, why. Four rules, and every one of them is about not
 * erasing something real:
 *
 * 1. A column you are already in does nothing. Repeating a destination is not
 *    an event.
 * 2. New and Designing are not destinations. They describe whether a design
 *    exists, and the only way to move a card back into them is to delete the
 *    work, which a drag must never do. The contract asks for a reviewed
 *    correction instead, and that is what the refusal says.
 * 3. Won is the customer saying yes, which is a real thing a person can record
 *    from the board. It does NOT approve a scope - approving a scope is a
 *    separate act with its own gate, in its own screen, and a card that could
 *    do it by being dragged would make that gate decorative.
 * 4. A card whose scope IS approved cannot be dragged out of Won. The
 *    approval is the reason it is there; moving the card would put the board
 *    and the frozen scope into disagreement, which is the one thing a derived
 *    board exists to prevent.
 */
export function moveTo(j: LeadFacts, to: LeadStage): Move {
  const from = leadStage(j)

  if (to === from) return { ok: false, does: '', because: 'It is already there.' }

  if (to === 'new' || to === 'designing') {
    return {
      ok: false,
      does: '',
      because:
        'These follow from whether anything has been drawn. Delete the design on the job itself if that is what you mean.',
    }
  }

  if (from === 'won' && j.scopeApproved) {
    return {
      ok: false,
      does: '',
      because:
        'The scope on this job has been approved. Approve a new scope, or reverse it on the job, rather than moving the card.',
    }
  }

  if (to === 'sent') {
    if (!j.hasPaper) {
      return { ok: false, does: '', because: 'There is no quote on this job to send.' }
    }
    return {
      ok: true,
      status: 'quoted',
      does: 'Records that the customer has the quote. It does not send anything.',
    }
  }

  if (to === 'won') {
    return {
      ok: true,
      status: 'won',
      does: 'Records that the customer said yes. It does not approve the scope, which is its own step on the job.',
    }
  }

  // lost
  return { ok: true, status: 'lost', does: 'Marks the job lost. Nothing is deleted.' }
}

export interface Column {
  stage: LeadStage
  label: string
  blurb: string
  cards: LeadFacts[]
}

/**
 * The board. Empty columns stay on screen and stay empty: the contract is
 * explicit that they show no records rather than inventing work, and a column
 * that disappears when it is empty is a column nobody can drop onto.
 */
export function board(jobs: LeadFacts[]): Column[] {
  return LEAD_STAGES.map((s) => ({
    stage: s.id,
    label: s.label,
    blurb: s.blurb,
    cards: jobs.filter((j) => leadStage(j) === s.id),
  }))
}

/** What a card says under the name, when there is anything worth saying. */
export function cardNote(j: LeadFacts): string | null {
  if (j.hasPaper && j.balance > 0 && leadStage(j) === 'won') return 'Still owed'
  if (j.scopeApproved) return 'Scope approved'
  return null
}
